import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import getLeverageColumns from './leverageColumns';
import { LEVERAGE_FILTER_FIELDS } from '../../constants/filters';
import { TrendingUp, Calendar, Download, Activity } from 'lucide-react';
import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import { LeverageRecord } from '../../Interfaces/mt5Interface';
import mt5Api from '../../services/mt5ApiService';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import EditLeverageModal from '../../components/accounts/EditLeverageModal';
import ViewLeverageDetailsModal from '../../components/accounts/ViewLeverageDetailsModal';

const LeverageHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [records, setRecords] = useState<LeverageRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedAccountForLeverage, setSelectedAccountForLeverage] = useState<{
    login: string | number;
    currentLeverage?: string | number;
    name?: string;
  } | null>(null);
  const [showEditLeverageModal, setShowEditLeverageModal] = useState(false);
  const [selectedRecordForView, setSelectedRecordForView] =
    useState<LeverageRecord | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const filterFields = LEVERAGE_FILTER_FIELDS;

  const fetchLeverageHistory = async () => {
    setLoading(true);
    try {
      const data = await mt5Api.getLeverageHistory();
      if (Array.isArray(data)) {
        const mapped: LeverageRecord[] = data.map((item: any) => {
          const dateObj = item.createdAt ? new Date(item.createdAt) : null;
          const changeDate = dateObj
            ? dateObj.toISOString().split('T')[0]
            : item.changeDate || '-';
          const changeTime = dateObj
            ? dateObj.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            : item.changeTime || '-';

          const rawGroup =
            item.account?.group || item.group || item.accountType || '';
          const groupSplit = rawGroup ? rawGroup.split(/[/\\]/)[0] : '-';

          return {
            id: String(item.id),
            mt5Login: item.mt5Login || item.account?.mt5Login || '',
            accountNumber:
              item.mt5Login ||
              item.account?.mt5Login ||
              item.accountNumber ||
              '-',
            accountType: groupSplit,
            group: rawGroup,
            oldLeverage:
              item.oldLeverage !== undefined
                ? item.oldLeverage
                : item.previousLeverage,
            newLeverage: item.newLeverage,
            previousLeverage:
              item.oldLeverage !== undefined
                ? item.oldLeverage
                : item.previousLeverage,
            changeDate,
            changeTime,
            reason: item.reason || 'Initial sync',
            status: item.status || 'approved',
            requestedBy:
              typeof item.changedBy === 'object' && item.changedBy?.fullName
                ? item.changedBy.fullName
                : item.changedBy || item.account?.name || 'System',
            approvedBy:
              typeof item.approvedBy === 'object' && item.approvedBy?.fullName
                ? item.approvedBy.fullName
                : item.approvedBy ||
                  (item.changedBy
                    ? typeof item.changedBy === 'object'
                      ? item.changedBy.fullName || 'Admin'
                      : String(item.changedBy)
                    : undefined),
            changedBy:
              typeof item.changedBy === 'object'
                ? item.changedBy?.fullName || item.changedBy?.email || '-'
                : item.changedBy || '-',
            riskLevel: item.riskLevel || 'low',
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            account: item.account,
          };
        });
        setRecords(mapped);
      }
    } catch (error) {
      console.error('Failed to fetch leverage history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeverageHistory();
  }, []);

  const handleExportData = () => {
    console.log('Export leverage history');
  };

  const handleViewDetails = (recordId: string) => {
    const record = records.find(
      r =>
        r.id === recordId ||
        r.mt5Login === recordId ||
        r.accountNumber === recordId
    );
    if (record) {
      setSelectedRecordForView(record);
      setShowViewModal(true);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const filteredRecords = records.filter(record => {
    const rawGroup =
      record.account?.group || record.group || record.accountType || '';
    const displayType = rawGroup ? rawGroup.split(/[/\\]/)[0] : '';
    const accountNumber = record.accountNumber || record.mt5Login || '';
    const oldLev = record.oldLeverage ?? record.previousLeverage;
    const oldLevStr =
      oldLev !== null && oldLev !== undefined ? String(oldLev) : '';
    const newLevStr =
      record.newLeverage !== null && record.newLeverage !== undefined
        ? String(record.newLeverage)
        : '';

    const matchesSearch =
      searchTerm === '' ||
      accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      displayType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      oldLevStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      newLevStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.changeDate || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (record.changeTime || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (record.reason || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.requestedBy || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (record.approvedBy || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (record.riskLevel || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAccountType =
      !activeFilters.accountType ||
      displayType.toLowerCase() === activeFilters.accountType.toLowerCase();

    const matchesStatus =
      !activeFilters.status ||
      (record.status || '').toLowerCase() ===
        activeFilters.status.toLowerCase();

    const matchesRiskLevel =
      !activeFilters.riskLevel ||
      (record.riskLevel || '').toLowerCase() ===
        activeFilters.riskLevel.toLowerCase();

    return (
      matchesSearch && matchesAccountType && matchesStatus && matchesRiskLevel
    );
  });

  const handleEditLeverage = (record: LeverageRecord) => {
    setSelectedAccountForLeverage({
      login: record.mt5Login || record.accountNumber || '',
      currentLeverage:
        record.newLeverage ??
        record.oldLeverage ??
        record.previousLeverage ??
        undefined,
      name: record.requestedBy,
    });
    setShowEditLeverageModal(true);
  };

  const columns = getLeverageColumns(handleViewDetails, handleEditLeverage);

  const totalChanges = records.length;
  const approvedCount = records.filter(
    r => (r.status || 'success').toLowerCase() === 'success'
  ).length;
  const pendingCount = records.filter(
    r => (r.status || '').toLowerCase() === 'open'
  ).length;

  return (
    <div className="relative">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Leverage History"
          description="Track all leverage changes and requests across your accounts"
        />
      </div>

      <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Changes"
          value={totalChanges.toString()}
          change="Total records"
          icon={Activity}
        />

        <StatsCard
          title="Approved"
          value={approvedCount.toString()}
          change={`${totalChanges > 0 ? Math.round((approvedCount / totalChanges) * 100) : 0}% approval rate`}
          icon={TrendingUp}
        />

        <StatsCard
          title="Pending"
          value={pendingCount.toString()}
          change="Awaiting review"
          icon={Calendar}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Leverage History</CardTitle>
            </div>
            <div className="flex space-x-2">
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button> */}
              <Button variant="outline" size="sm" onClick={handleExportData}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Leverage History"
          />
          {loading ? (
            <LoadingSkeleton type="table" rows={5} cols={9} />
          ) : (
            <DataTable
              columns={columns}
              data={filteredRecords}
              loading={loading}
              searchValue={searchTerm}
              searchPlaceholder="Search by account number, status, requested by..."
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              showColumnVisibility={true}
              showAddButton={false}
              showExportButton={false}
              initialPageSize={10}
              initialSortBy={{ id: 'changeDate', desc: true }}
            />
          )}
        </CardContent>
      </Card>

      <EditLeverageModal
        isOpen={showEditLeverageModal}
        onClose={() => {
          setShowEditLeverageModal(false);
          setSelectedAccountForLeverage(null);
        }}
        onSuccess={() => {
          fetchLeverageHistory();
          setShowEditLeverageModal(false);
          setSelectedAccountForLeverage(null);
        }}
        account={selectedAccountForLeverage}
      />

      <ViewLeverageDetailsModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedRecordForView(null);
        }}
        record={selectedRecordForView}
      />
    </div>
  );
};

export default LeverageHistory;
